import { slDbg } from './config';
import { flushEventsBeacon, trackEvent } from './events';

/**
 * Best-effort launcher that breaks the visitor out of an in-app webview
 * (TikTok / Facebook / Instagram / Line / Feishu / WeChat …) into the host
 * system's default browser.
 *
 * Each in-app webview has its own quirks for breaking out:
 *  - **iOS Safari scheme** (`x-safari-https://...`): general-purpose,
 *    works in TikTok, Line and most. Must be triggered via `<a>.click()`
 *    for Facebook (FB intercepts `location.href` writes).
 *  - **iOS Instagram**: only honors a `<meta http-equiv="refresh">`.
 *  - **Android Chrome intent**: needs `action + component + package` all set
 *    (just `package=com.android.chrome` is silently ignored by newer
 *    webviews — `component=com.android.chrome` pins to Chrome's main
 *    activity which is what allows the launch).
 */

function isIOS(ua: string) {
  return /iphone|ipad|ipod/i.test(ua);
}

function isAndroid(ua: string) {
  return /android/i.test(ua);
}

// Scope: the ad platforms we actually run on (TikTok / Facebook / Instagram)
// plus Feishu (Lark) — kept only so it's detectable for our own QA testing.
const IN_APP_WEBVIEW_RX =
  /BytedanceWebview|musical_ly|aweme|tiktok|trill|ttwebview|FBAN|FBIOS|FBAV|Instagram|Lark|Feishu|LarkLocale/i;

const FB_WEBVIEW_RX = /FBAN|FBIOS|FBAV/;
const INSTAGRAM_WEBVIEW_RX = /Instagram/;

const KNOWN_APP_RX: Array<[RegExp, string]> = [
  [/Lark|Feishu|LarkLocale/, 'feishu'],
  [/BytedanceWebview|musical_ly|aweme|tiktok|ttwebview|trill/, 'tiktok'],
  [FB_WEBVIEW_RX, 'facebook'],
  [INSTAGRAM_WEBVIEW_RX, 'instagram'],
];

export function detectAppName(ua: string = navigator.userAgent): string | undefined {
  for (const [rx, name] of KNOWN_APP_RX) if (rx.test(ua)) return name;
  return undefined;
}

export function isInAppWebview(ua: string = navigator.userAgent): boolean {
  return IN_APP_WEBVIEW_RX.test(ua);
}

function isStandaloneChrome(ua: string): boolean {
  return (
    isAndroid(ua) &&
    /Chrome\/\d/i.test(ua) &&
    !IN_APP_WEBVIEW_RX.test(ua) &&
    !/SamsungBrowser|OPR\/|EdgA\/|Firefox|Brave|Vivaldi|wv\)/i.test(ua)
  );
}

export function needsSchemeBreakout(ua: string = navigator.userAgent): boolean {
  // iOS: always attempt the x-safari breakout. Modern in-app webviews — including
  // TikTok's ad-preview (SFSafariViewController) and recent TikTok/Line WKWebviews —
  // now present a UA byte-identical to standalone Safari (`Version/xx Mobile/15E148
  // Safari/604.1`, NO in-app token), so there is NO reliable UA signal to tell a
  // trapped webview from real Safari. Since ~all traffic here arrives via an in-app
  // ad webview (organic standalone-Safari hits ≈ 0), we bias to breakout: x-safari
  // escapes the trapped webview, and in genuine Safari it degrades to a normal nav.
  // (2026-07 fix: a clean-UA TikTok ad-preview was mis-classified as Safari →
  //  inline redirect → visitor trapped inside the webview instead of Safari.)
  if (isIOS(ua)) return true;
  if (isAndroid(ua)) return !isStandaloneChrome(ua);
  return false;
}

function track(
  kind:
    | 'redirectAttempt'
    | 'redirectAppSwitched'
    | 'redirectFallback'
    | 'redirectGiveUp'
    | 'redirectInline'
    | 'redirectLateSwitch',
  data: Record<string, unknown>,
) {
  // Buffer-only. sendBeacon between gesture and intent kills the "open in
  // external app" dialog in Feishu / WeChat. Flush deferred to macrotask.
  trackEvent(kind, data);
}

function lateFlush() {
  window.setTimeout(() => flushEventsBeacon(), 0);
}

// An external app (Chrome) taking foreground is detected via several signals,
// because many Android in-app webviews (Instagram/Facebook) do NOT fire
// `visibilitychange` / flip `visibilityState` to "hidden" when they background —
// relying on that single signal misses most successful switches (RB real-page
// telemetry shows ~86-95% actually reach Chrome, vs ~55% detected here before).
//   - visibilitychange -> hidden : reliable when the webview implements it
//   - pagehide                   : reliable backgrounding/unload signal
//   - window blur                : catches the webviews that do neither, but can
//     fire spuriously (input focus changes), so it is re-checked after a short
//     delay — only a still-unfocused page counts as a real switch.
function waitForAppSwitch(timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    const cleanup = () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onAway);
      window.removeEventListener('blur', onBlur);
    };
    const settle = (switched: boolean) => {
      if (done) return;
      done = true;
      cleanup();
      resolve(switched);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') settle(true);
    };
    const onAway = () => settle(true);
    const onBlur = () => {
      // Debounce: a transient blur (e.g. focus moving within the page) regains
      // focus immediately; a real app switch leaves the page unfocused.
      window.setTimeout(() => {
        if (!done && !document.hasFocus()) settle(true);
      }, 120);
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onAway);
    window.addEventListener('blur', onBlur);
    window.setTimeout(() => settle(false), timeoutMs);
  });
}

/**
 * Launch a custom-scheme URL using an <a>.click() — required for Facebook
 * iOS webview which silently swallows `location.href = '<scheme>...'`.
 */
function clickAnchor(href: string): void {
  const a = document.createElement('a');
  a.href = href;
  // Keep it off-screen so it doesn't visually flash.
  a.style.position = 'fixed';
  a.style.top = '-9999px';
  a.style.left = '-9999px';
  document.body.appendChild(a);
  a.click();
  // Remove on next tick so the click definitely registers.
  window.setTimeout(() => a.remove(), 0);
}

/**
 * Inject a <meta http-equiv="refresh"> with a custom scheme — last-resort
 * trick for Instagram which blocks every other break-out method.
 */
function injectMetaRefresh(target: string): void {
  const meta = document.createElement('meta');
  meta.setAttribute('http-equiv', 'refresh');
  meta.setAttribute('content', `0;url=${target}`);
  document.head.appendChild(meta);
}

function safariSchemeFor(url: string): string {
  if (url.startsWith('https://')) return `x-safari-${url}`; // -> x-safari-https://...
  if (url.startsWith('http://')) return `x-safari-${url}`; // -> x-safari-http://...
  return url;
}

function chromeIntentFor(url: string): string {
  const u = new URL(url);
  const scheme = u.protocol.replace(':', '');
  const tail = `${u.host}${u.pathname}${u.search}${u.hash}`;
  // NB: `component=com.android.chrome` (just the package, no /Activity suffix).
  // Roibest's working implementation uses this exact form — adding a specific
  // Activity name can mismatch across Chrome versions and Android silently
  // ignores the intent. Keep it short.
  return `intent://${tail}#Intent;scheme=${scheme};action=android.intent.action.VIEW;component=com.android.chrome;package=com.android.chrome;end`;
}

// Navigation side-effects behind a seam so tests can observe redirects without
// jsdom's unimplemented window.location navigation throwing.
export const nav = {
  assign(url: string) {
    window.location.href = url;
  },
  replace(url: string) {
    window.location.replace(url);
  },
};

// chrome-intent frequently can't launch Chrome from an Android System WebView; if
// no app switch is seen within this window, fall back to an inline https redirect.
// 3500ms (was 2500) gives a cold-start Chrome time to foreground — shorter windows
// recorded late switches as fallbacks (~19% of fallbacks were verified late switches).
const ANDROID_SWITCH_TIMEOUT_MS = 3500;
// iOS breakout 后观察是否切到 Safari 的窗口,仅用于埋点(appSwitched vs giveUp),
// 不再做 inline 兜底(见下方 iOS 分支说明)。
const IOS_SWITCH_TIMEOUT_MS = 8000;
// After a fallback is recorded, keep watching briefly: if the page backgrounds now,
// Chrome did open (just late) — emit redirectLateSwitch to keep the success metric
// honest. A foreground page navigates away via inlineRedirect and this never fires;
// a backgrounded one defers that navigation, leaving this observer alive to catch it.
const LATE_SWITCH_OBSERVE_MS = 2500;

// Inline https redirect. Flushes telemetry BEFORE replace — replace unloads the
// page synchronously and would otherwise drop the buffered events.
function inlineRedirect(url: string): void {
  flushEventsBeacon();
  nav.replace(url);
}

// 同步函数(内部无 await,waitForAppSwitch 都是 fire-and-forget 的 void .then)。
// 关键:必须同步 —— iOS WebKit 对 custom-scheme(x-safari)导航要求"用户手势的直接
// 同步栈",穿过 async function 边界会让 transient activation 失效、导致 scheme 被拒、
// 停在 webview 内(而非唤起 Safari)。切勿改回 async。
export function openInSystemBrowser(url: string): void {
  const ua = navigator.userAgent;
  const app = detectAppName(ua) ?? 'unknown';
  const breakout = needsSchemeBreakout(ua);
  slDbg(`openBrowser app=${app} ios=${isIOS(ua)} breakout=${breakout} url=${url}`);

  if (!breakout) {
    slDbg('inline (already in target browser)');
    track('redirectInline', { reason: 'already-in-target-browser' });
    inlineRedirect(url);
    return;
  }

  // ===================== iOS =====================
  if (isIOS(ua)) {
    const safariUrl = safariSchemeFor(url);
    if (INSTAGRAM_WEBVIEW_RX.test(ua)) {
      injectMetaRefresh(safariUrl);
      track('redirectAttempt', { method: 'meta-refresh', target: safariUrl, app });
    } else if (FB_WEBVIEW_RX.test(ua)) {
      clickAnchor(safariUrl);
      track('redirectAttempt', { method: 'anchor-click', target: safariUrl, app });
    } else {
      // TikTok / Line / 广告预览(SFSafariViewController)等 —— 含 UA 伪装成纯 Safari 的
      // webview:用 location.href 写 x-safari scheme。iOS 实测这个能唤起系统 Safari
      // (调试页验证:location.href=x-safari 直接弹"在 Safari 打开?")。needsSchemeBreakout
      // 现在对所有 iOS 返回 true,故此分支也覆盖"UA 看着像 Safari 实为 in-app webview"的
      // 情形(2026-07 广告预览踩坑:UA 是 Version/26.5 Safari/604.1 却困在 webview 内)。
      nav.assign(safariUrl);
      track('redirectAttempt', { method: 'safari-scheme', target: safariUrl, app });
    }
    slDbg(`ios breakout attempted url=${safariUrl}`);
    lateFlush();
    // 不做 inline 兜底 —— nav.assign(x-safari) 实测能唤起系统 Safari,而 inline 会在
    // webview 内打开(正是不想要的),且 iOS 弹原生"在 Safari 打开?"对话框时 visibilityState
    // 仍 visible、切换检测本就不可靠,inline 会抢在用户确认前打断。这里只观察记录是否切走;
    // scheme 若被个别 webview 吞掉,访客停在页面(verify 已 pass、按钮仍在,可重点),不
    // 强行 webview 内打开。
    void waitForAppSwitch(IOS_SWITCH_TIMEOUT_MS).then((switched) => {
      slDbg(`ios waitForAppSwitch switched=${switched}`);
      if (switched) track('redirectAppSwitched', { app, os: 'ios' });
      else track('redirectGiveUp', { app, os: 'ios', reason: 'no-switch' });
      flushEventsBeacon();
    });
    return;
  }

  // ===================== Android =====================
  if (isAndroid(ua)) {
    let chromeIntent: string;
    try {
      chromeIntent = chromeIntentFor(url);
    } catch {
      // Unparseable URL — can't build an intent; an inline https redirect is the
      // best we can do (still better than stranding the visitor).
      track('redirectFallback', { app, os: 'android', target: url, reason: 'url-parse-error' });
      inlineRedirect(url);
      return;
    }
    // FIRE FIRST — synchronous, no preceding network/sendBeacon.
    nav.assign(chromeIntent);
    track('redirectAttempt', { method: 'chrome-intent', target: chromeIntent, app });
    lateFlush();
    void waitForAppSwitch(ANDROID_SWITCH_TIMEOUT_MS).then((switched) => {
      if (switched) {
        track('redirectAppSwitched', { app, os: 'android', via: 'chrome' });
        flushEventsBeacon();
        return;
      }
      // chrome-intent didn't switch apps — common in Android System WebView, which
      // can't launch Chrome. Don't give up: fall back to an inline https redirect.
      // A Play link is still handled by Play's intent-filter; otherwise the target
      // at least opens in-webview instead of stranding the visitor on the landing.
      track('redirectFallback', { app, os: 'android', target: url });
      // Keep observing: a switch detected now means Chrome opened late (not a real
      // failure). Record it for metric accuracy before the inline redirect.
      void waitForAppSwitch(LATE_SWITCH_OBSERVE_MS).then((late) => {
        if (late) {
          track('redirectLateSwitch', { app, os: 'android' });
          flushEventsBeacon();
        }
      });
      inlineRedirect(url);
    });
    return;
  }

  // Other (desktop preview tools etc.)
  track('redirectInline', { reason: 'unknown-os', app });
  inlineRedirect(url);
}

/**
 * 跳转真实/审核页的唯一出口 —— 按链接配置决定是否做 scheme breakout。
 *
 * keepInAppBrowser=true 的链接刻意不踢访客去系统浏览器:被踢进 Chrome 之后,
 * 下游 DC 中间页判定 isChromeBrowser 为真、直接跳过回流页,于是"真实链接=回流
 * 链接"的投放永远等不到回流页弹出。留在 webview 里跳,回流页才按预期出现。
 *
 * 默认(false)完全走 openInSystemBrowser,存量链接行为不变。
 */
export function redirectToTarget(url: string, keepInAppBrowser = false): void {
  if (!keepInAppBrowser) {
    openInSystemBrowser(url);
    return;
  }
  slDbg(`inline (keepInAppBrowser) url=${url}`);
  track('redirectInline', { reason: 'keep-in-app-browser', app: detectAppName() ?? 'unknown' });
  inlineRedirect(url);
}

/**
 * 本次跳转是否会真的走 scheme breakout。keepInAppBrowser 关掉 breakout 之后,
 * 调用方那些"breakout 在途就别 inline replace"的兜底判断必须跟着改口径,
 * 否则会以为访客正被系统浏览器带走、白白停在落地页。
 */
export function willBreakOut(keepInAppBrowser = false, ua: string = navigator.userAgent): boolean {
  return !keepInAppBrowser && needsSchemeBreakout(ua);
}
